/**
 * [INPUT]: 依赖 supabaseAdmin、fetchAllPaginated、BJ 日切工具
 * [OUTPUT]: 对外提供 dashboardService.fetchOverview（Zinner 运营指标）
 * [POS]: services/supabase Dashboard 聚合真相源
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

import { supabaseAdmin } from './index'
import { fetchAllPaginated, wrap } from './base'

const BJ_OFFSET_MS = 8 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

function bjDayKey(isoOrDate) {
  const d = new Date(isoOrDate)
  const bj = new Date(d.getTime() + BJ_OFFSET_MS)
  const y = bj.getUTCFullYear()
  const m = String(bj.getUTCMonth() + 1).padStart(2, '0')
  const day = String(bj.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function bjTodayStartUtc() {
  const bjNow = new Date(Date.now() + BJ_OFFSET_MS)
  const y = bjNow.getUTCFullYear()
  const m = bjNow.getUTCMonth()
  const d = bjNow.getUTCDate()
  return new Date(Date.UTC(y, m, d) - BJ_OFFSET_MS).toISOString()
}

function daysAgoStartUtc(days) {
  return new Date(new Date(bjTodayStartUtc()).getTime() - (days - 1) * DAY_MS).toISOString()
}

function emptySeries(days) {
  const start = new Date(daysAgoStartUtc(days)).getTime()
  return Array.from({ length: days }, (_, i) => {
    const key = bjDayKey(new Date(start + i * DAY_MS))
    return { day: key, users: 0, prospects: 0, dateLogs: 0, messages: 0, chatUsers: 0, sessions: 0 }
  })
}

function bump(map, key, field, n = 1) {
  if (!map[key]) map[key] = { day: key, users: 0, prospects: 0, dateLogs: 0, messages: 0, chatUsers: 0, sessions: 0 }
  map[key][field] += n
}

export const dashboardService = {
  async fetchOverview({ days = 30 } = {}) {
    if (!supabaseAdmin) throw new Error('supabaseAdmin 未配置')

    const since = daysAgoStartUtc(days)
    const todayStart = bjTodayStartUtc()

    const [
      profiles,
      prospects,
      dateLogs,
      topics,
      memories,
      sms,
      chatRpc,
    ] = await Promise.all([
      fetchAllPaginated(() =>
        supabaseAdmin.from('profiles').select('user_id, created_at').gte('created_at', since),
      ),
      fetchAllPaginated(() =>
        supabaseAdmin
          .from('prospects')
          .select('id, status, created_at, deleted_at')
          .is('deleted_at', null)
          .gte('created_at', since),
      ),
      fetchAllPaginated(() =>
        supabaseAdmin
          .from('date_logs')
          .select('id, user_id, created_at, deleted_at')
          .is('deleted_at', null)
          .gte('created_at', since),
      ),
      fetchAllPaginated(() =>
        supabaseAdmin.from('topics').select('id, metadata, deleted_at').is('deleted_at', null),
      ),
      fetchAllPaginated(() =>
        supabaseAdmin.from('prospect_memories').select('prospect_id, doc'),
      ),
      fetchAllPaginated(() =>
        supabaseAdmin
          .from('sms_verifications')
          .select('id, verified, created_at')
          .gte('created_at', since),
      ),
      supabaseAdmin.rpc('cms_chat_daily_stats', { days }).then(({ data, error }) => {
        if (error) {
          console.warn('[dashboard] cms_chat_daily_stats', error.message)
          return []
        }
        return data || []
      }),
    ])

    const [{ count: totalUsers }, { count: totalProspects }, { count: totalDateLogs }] =
      await Promise.all([
        supabaseAdmin.from('profiles').select('user_id', { count: 'exact', head: true }),
        supabaseAdmin
          .from('prospects')
          .select('id', { count: 'exact', head: true })
          .is('deleted_at', null),
        supabaseAdmin
          .from('date_logs')
          .select('id', { count: 'exact', head: true })
          .is('deleted_at', null),
      ])

    const todayUsers = profiles.filter((p) => p.created_at >= todayStart).length
    const activeToday = new Set(
      dateLogs.filter((d) => d.created_at >= todayStart).map((d) => d.user_id),
    ).size

    const smsTotal = sms.length
    const smsVerified = sms.filter((s) => s.verified).length
    const smsSuccessRate = smsTotal ? smsVerified / smsTotal : 0

    const statusDist = { Exploring: 0, Dating: 0, Steady: 0, Archived: 0 }
    for (const p of prospects) {
      if (statusDist[p.status] != null) statusDist[p.status] += 1
    }
    // also count all-time status for pie — refetch light
    const { data: allStatus } = await supabaseAdmin
      .from('prospects')
      .select('status')
      .is('deleted_at', null)
    const statusPie = { Exploring: 0, Dating: 0, Steady: 0, Archived: 0 }
    for (const p of allStatus || []) {
      if (statusPie[p.status] != null) statusPie[p.status] += 1
    }

    const discussed = topics.filter((t) => t.metadata?.discussed === true).length
    const discussedRate = topics.length ? discussed / topics.length : 0

    const withDoc = memories.filter((m) => m.doc && String(m.doc).trim().length > 0).length
    const memoryCoverage = totalProspects ? withDoc / totalProspects : 0

    const byDay = Object.fromEntries(emptySeries(days).map((r) => [r.day, { ...r }]))
    for (const p of profiles) bump(byDay, bjDayKey(p.created_at), 'users')
    for (const p of prospects) bump(byDay, bjDayKey(p.created_at), 'prospects')
    for (const d of dateLogs) bump(byDay, bjDayKey(d.created_at), 'dateLogs')

    const growthSeries = Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day))

    const chatByDay = Object.fromEntries(emptySeries(days).map((r) => [r.day, { ...r }]))
    for (const row of chatRpc) {
      const key = typeof row.day === 'string' ? row.day.slice(0, 10) : bjDayKey(row.day)
      if (!chatByDay[key]) continue
      chatByDay[key].messages = Number(row.messages || 0)
      chatByDay[key].sessions = Number(row.sessions || 0)
      chatByDay[key].chatUsers = Number(row.chat_users || 0)
    }
    const chatSeries = Object.values(chatByDay).sort((a, b) => a.day.localeCompare(b.day))
    const chatDauToday = chatSeries.at(-1)?.chatUsers || 0

    const smsFunnel = [
      { name: '发送', value: smsTotal },
      { name: '验证成功', value: smsVerified },
    ]

    return wrap.success({
      tiles: {
        totalUsers: totalUsers || 0,
        todayUsers,
        totalProspects: totalProspects || 0,
        chatDauToday: chatDauToday || activeToday,
        totalDateLogs: totalDateLogs || 0,
        smsSuccessRate,
      },
      growthSeries,
      chatSeries,
      smsFunnel,
      statusPie,
      meta: {
        discussedRate,
        memoryCoverage,
        activeToday,
        days,
      },
    })
  },
}

export default dashboardService
